import { toastr } from "react-redux-toastr";
import { useForm } from "../../hooks/useForm";
import {
  useGenerarReporteEmisionMutation,
  useGetReportesEmisionQuery,
} from "../../services/docs";
import { Spinner } from "../../components/Spinner";

export const ReportesEmisionesScreen = () => {
  // Queries & Mutations
  const { data, isLoading } = useGetReportesEmisionQuery();
  const [generarReporteAction, { isLoading: isLoadingBusqueda }] = useGenerarReporteEmisionMutation();

  // Custom Form Hook
  const { formulario, handleInputChange, handleSelectChange } = useForm({
    fi_tipo_reporte: 0,
    fc_fecha_inicio: "",
    fc_fecha_fin: "",
    fc_nomenclatura: "",
    fi_id: "",
    fc_valor: "",
  });

  const tipoReporte = Number(formulario.fi_tipo_reporte);

  const generarReporte = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    // 1. Validaciones
    if (tipoReporte === 0) {
      toastr.error("Debe seleccionar un tipo de Reporte");
      return;
    }

    if (tipoReporte === 1) {
      if (!formulario.fc_fecha_inicio || !formulario.fc_fecha_fin) {
        toastr.error("Debe agregar Fecha Inicial y Final para este reporte");
        return;
      }
      if (new Date(formulario.fc_fecha_inicio) > new Date(formulario.fc_fecha_fin)) {
        toastr.warning("La Fecha Inicial no puede ser posterior a la Fecha Final");
        return;
      }
    }

    if (tipoReporte > 1 && !formulario.fc_valor.trim()) {
      toastr.error("Debe agregar un valor de búsqueda para este reporte");
      return;
    }

    // 2. Construcción segura del Payload (Sin mutar el estado original)
    const payload = {
      ...formulario,
      fi_tipo_reporte: tipoReporte,
      fc_nomenclatura: tipoReporte === 2 ? formulario.fc_valor : "",
      fi_id: tipoReporte === 3 ? formulario.fc_valor : "0",
    };
    delete payload.fc_valor; // Borramos fc_valor de la copia, no del estado

    // 3. Envío y Descarga
    try {
      const reporte = await generarReporteAction(payload).unwrap();

      if (reporte?.archivo) {
        // Descarga directa vía DOM
        const enlaceTemporal = document.createElement("a");
        enlaceTemporal.href = reporte.archivo;
        enlaceTemporal.target = "_blank";
        enlaceTemporal.style.display = "none";
        document.body.appendChild(enlaceTemporal);
        enlaceTemporal.click();
        document.body.removeChild(enlaceTemporal);

        toastr.success("Reporte generado y descargado exitosamente");
      } else {
        toastr.info(reporte?.message || "Reporte generado, pero no se recibió archivo");
      }
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      } else {
        toastr.error("Ocurrió un error al generar el reporte");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* --- Encabezado y Filtros --- */}
      <section className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm">

        {/* Cabecera */}
        <div className="flex flex-col gap-2 border-b border-slate-100 px-7 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold tracking-tight text-slate-900">
              <svg className="h-5 w-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
              Reportes de Emisiones
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Genera y descarga reportes operativos de emisiones en formato consolidado.
            </p>
          </div>
        </div>

        {/* Formulario */}
        <form onSubmit={generarReporte} className="bg-slate-50/50 p-6">
          <div className="grid grid-cols-1 items-end gap-5 lg:grid-cols-12">

            {/* Select: Tipo de Búsqueda */}
            <div className="lg:col-span-4">
              <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-600" htmlFor="fi_tipo_reporte">
                Criterio de Búsqueda
              </label>
              <select
                id="fi_tipo_reporte"
                name="fi_tipo_reporte"
                onChange={handleSelectChange}
                value={formulario.fi_tipo_reporte}
                className="h-11 w-full cursor-pointer rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 transition-all hover:bg-slate-50 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              >
                <option value={0} disabled>Selecciona el tipo de reporte...</option>
                {data?.reportes?.map((b: any) => (
                  <option key={b.id} value={b.id}>
                    {b.fc_tipo_reporte}
                  </option>
                ))}
              </select>
            </div>

            {/* Inputs Condicionales: Fecha Inicial y Final (Tipo 1) */}
            {tipoReporte === 1 && (
              <>
                <div className="lg:col-span-3">
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-600" htmlFor="fc_fecha_inicio">
                    Fecha Inicial
                  </label>
                  <input
                    id="fc_fecha_inicio"
                    name="fc_fecha_inicio"
                    type="date"
                    onChange={handleInputChange}
                    value={formulario.fc_fecha_inicio}
                    className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 transition-all focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                  />
                </div>
                <div className="lg:col-span-3">
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-600" htmlFor="fc_fecha_fin">
                    Fecha Final
                  </label>
                  <input
                    id="fc_fecha_fin"
                    name="fc_fecha_fin"
                    type="date"
                    onChange={handleInputChange}
                    value={formulario.fc_fecha_fin}
                    className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 transition-all focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                  />
                </div>
              </>
            )}

            {/* Input Condicional: Valor a Buscar (Tipo > 1) */}
            {tipoReporte > 1 && (
              <div className="lg:col-span-5">
                <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-600" htmlFor="fc_valor">
                  Valor a buscar
                </label>
                <input
                  id="fc_valor"
                  name="fc_valor"
                  type="text"
                  placeholder="Ingrese el valor de búsqueda..."
                  onChange={handleInputChange}
                  value={formulario.fc_valor}
                  className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 transition-all focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
            )}

            {/* Botón Generar */}
            <div className={`lg:col-span-2 ${tipoReporte === 0 ? "lg:col-start-5" : ""}`}>
              <button
                type="submit"
                disabled={isLoadingBusqueda}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-brand-700 disabled:opacity-70"
              >
                {isLoadingBusqueda ? (
                  <Spinner />
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>
                    <span>Descargar</span>
                  </>
                )}
              </button>
            </div>

          </div>
        </form>
      </section>

      {/* Loading State Global */}
      {isLoadingBusqueda && (
        <div className="flex w-full flex-col items-center justify-center py-12 text-slate-500">
          <Spinner />
          <span className="mt-3 text-sm font-medium">Procesando y generando documento...</span>
        </div>
      )}

    </div>
  );
};
